import React, {useContext, useEffect, useState} from 'react';
import {CustomDomainScreenContext} from './base';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {CustomDomainState} from '../../api/types/organization/CustomDomain';
import {BoxWithTitle} from './list';
import {SettingsTitle} from './verify';
import {Input} from '../settings/profile/Profile';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from 'src/ui-components/Select';
import {ReactComponent as PerceptPixelLogo} from 'src/assets/icons/logo.svg';
import {StorageIcon} from '../RemoteStorage/screen';
import {Button} from '../../ui-components/Button/button';
import {toast} from 'react-toastify';
import {StorageType} from '../../api/types/organization/StorageSource';

const defaultSourceValue = 'default.' + Date.now();
type LoadingState = 'idle' | 'pending' | 'resolved' | 'rejected';

export default function CustomDomainSettings() {
    const {list, storageSourceList, api, onDomainUpdate} = useContext(CustomDomainScreenContext);
    const {hostname} = useParams();
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const [loading, setLoading] = useState<LoadingState>('idle');

    const domain = list.find((d) => d.hostname === hostname);
    const [storageSource, setStorageSource] = useState(domain?.organization_storage_source?.name ?? defaultSourceValue);
    const [enableMediaUrls, setEnableMediaUrls] = useState(domain?.enable_media_urls ?? false);

    useEffect(() => {
        if (!domain) {
            navigate('/domains');
            return;
        }
        switch (domain.state) {
            case CustomDomainState.CNAME_PENDING:
                navigate(`/domains/verify/${domain.hostname}`);
                break;
            case CustomDomainState.CNAME_VERIFIED:
            case CustomDomainState.DOMAIN_PENDING:
            case CustomDomainState.DOMAIN_VERIFIED:
                navigate(`/domains/verify/${domain.hostname}`);
                break;
            case CustomDomainState.DOMAIN_READY:
                break;
            case CustomDomainState.DOMAIN_ERROR:
            default:
                navigate('/domains');
                break;
        }
    }, [domain, navigate]);

    return domain?.state === CustomDomainState.DOMAIN_READY ? (
        <BoxWithTitle title={`${SettingsTitle(domain.state)}: ${domain.hostname}`}>
            <div className={'flex flex-col p-6'}>
                {sp.get('source') === 'verify' ? (
                    <div className={'flex flex-col pb-10'}>
                        <div
                            className={
                                'flex flex-col rounded-md border border-gray-300 bg-green-300 p-4 leading-relaxed'
                            }>
                            <span className={'text-lg'}>Domain Verified</span>
                            <span>Your domain has been provisioned and is ready to use.</span>
                        </div>
                    </div>
                ) : null}
                <div className={'flex flex-col pb-10'}>
                    <span className={'pb-1.5 text-sm font-medium text-gray-700'}>Hostname</span>
                    <Input type='text' value={domain.hostname} disabled={true} />
                </div>
                <div className={'flex flex-col pb-10'}>
                    <span className={'pb-1.5 text-sm font-medium text-gray-700'}>Storage</span>
                    <Select
                        value={storageSource}
                        onValueChange={(nv) => {
                            setStorageSource(nv);
                        }}>
                        <SelectTrigger className='w-[180px]'>
                            <SelectValue placeholder='Select a Storage Source' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel>Storage Source</SelectLabel>
                                <SelectItem value={defaultSourceValue}>
                                    <div className={'flex flex-row items-center'}>
                                        <PerceptPixelLogo className={'h-4 w-4'} />
                                        <span className={'pl-2 italic'}>Default</span>
                                    </div>
                                </SelectItem>
                                {storageSourceList.map((s) => (
                                    <SelectItem key={s.name} value={s.name}>
                                        <div className={'flex flex-row items-center'}>
                                            <StorageIcon className={'h-4 w-4'} type={s.type} />
                                            <span className={'pl-2'}>{s.name}</span>
                                            {s.type === StorageType.REMOTE_STORAGE ? (
                                                <span className={'pl-2 text-xs italic text-gray-500'}>{s.origin}</span>
                                            ) : null}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>
                <div className={'flex flex-col pb-10'}>
                    <div className={'flex flex-row items-center gap-2'}>
                        <input
                            type='checkbox'
                            checked={enableMediaUrls}
                            onChange={(e) => {
                                setEnableMediaUrls(e.target.checked);
                            }}
                        />
                        <div className={'flex flex-col'}>
                            <span className={'text-sm font-medium text-gray-700'}>Enable Media URLs</span>
                            <span className={'text-sm text-gray-500'}>Use Custom Domain for Media URLs</span>
                        </div>
                    </div>
                </div>
                <div className={'flex flex-col pb-10'}>
                    <Button
                        data-disabled={
                            loading === 'pending' ||
                            (storageSource === domain?.organization_storage_source?.name &&
                                enableMediaUrls === domain?.enable_media_urls)
                        }
                        disabled={
                            loading === 'pending' ||
                            (storageSource === domain?.organization_storage_source?.name &&
                                enableMediaUrls === domain?.enable_media_urls)
                        }
                        className={'data-[disabled="true"]:bg-gray-300'}
                        onClick={() => {
                            setLoading('pending');
                            api
                                ?.update({
                                    hostname: domain.hostname,
                                    org_storage_source: storageSource === defaultSourceValue ? '' : storageSource,
                                    enable_media_urls: enableMediaUrls,
                                })
                                .then((nd) => {
                                    setLoading('resolved');
                                    toast('Domain storage source updated', {type: 'success'});
                                    onDomainUpdate(nd);
                                })
                                .catch((err) => {
                                    setLoading('rejected');
                                    toast(`Failed to update domain storage source: ${err.message}`, {type: 'error'});
                                    setStorageSource(domain?.organization_storage_source?.name ?? defaultSourceValue);
                                });
                        }}>
                        {loading === 'pending' ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </BoxWithTitle>
    ) : null;
}
